import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, supabaseConfigError, supabaseUrl } from '@/lib/supabase';
import { setSentryUser } from '@/lib/sentry';
import type { User, Session } from '@supabase/supabase-js';

export type AdminRole = 'superadmin' | 'admin' | 'support';

/**
 * True when the request never reached the database. PostgREST and Postgres both
 * populate `code` on a real response, so an error without one means the call
 * failed in transport: bad hostname, DNS failure, CORS, or the backend is down.
 */
function isConnectionError(error: { code?: string } | null): boolean {
  return !!error && !error.code;
}

function describeConnectionFailure(detail: string): string {
  return (
    `Cannot reach the BlazePOS backend at ${supabaseUrl}. ` +
    'This is a connectivity or configuration problem, not a rejected sign-in. ' +
    `Details: ${detail}`
  );
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, loading };
}

export function useAdminUser() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['admin-user', user?.id],
    queryFn: async () => {
      if (!user?.email) return null;

      // Use RPC to query from the blazepos schema directly
      const { data, error } = await supabase.rpc('get_admin_user_by_email' as never, {
        p_email: user.email,
      } as never);

      if (error) {
        // A transport failure tells us nothing about this account. Throw so the
        // caller can say the backend is unreachable instead of rendering an
        // "access denied" screen at a user whose access is fine.
        if (isConnectionError(error)) {
          throw new Error(describeConnectionFailure(error.message));
        }

        // Fallback to direct query if RPC doesn't exist
        const { data: directData, error: directError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('email', user.email as never)
          .eq('is_active', true as never)
          .single();

        if (directError) {
          if (isConnectionError(directError)) {
            throw new Error(describeConnectionFailure(directError.message));
          }
          console.error('Admin user fetch error:', directError);
          return null;
        }
        return directData as unknown as AdminUser;
      }

      const result = data as unknown as AdminUser[] | null;
      return result?.[0] || null;
    },
    enabled: !!user?.email,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      if (supabaseConfigError) {
        throw new Error(supabaseConfigError);
      }

      // First, check if user is in admin_users and is active using RPC
      const { data: adminUsers, error: adminError } = await supabase.rpc(
        'get_admin_user_by_email' as never,
        { p_email: email } as never
      );

      // Only an actual response from the database can tell us whether this
      // person is an admin. Reporting a transport failure as "access denied"
      // sends people hunting for a permissions problem that does not exist.
      if (adminError) {
        if (isConnectionError(adminError)) {
          throw new Error(describeConnectionFailure(adminError.message));
        }
        throw new Error(`Could not verify admin access: ${adminError.message}`);
      }

      const adminUser = (adminUsers as AdminUser[] | null)?.[0];

      if (!adminUser) {
        throw new Error('Access denied. You are not authorized to access this dashboard.');
      }

      // Now sign in with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Update last login using RPC
      await supabase.rpc('update_admin_user_login' as never, { p_email: email } as never);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user'] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      // Verify current password by re-authenticating
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('Not authenticated');

      // Try to sign in with current password to verify
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (verifyError) {
        throw new Error('Current password is incorrect');
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { success: true };
    },
  });
}

// Admin user management
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_admin_users' as never);

      if (error) throw error;
      return (data as unknown as AdminUser[]) || [];
    },
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      name,
      role,
    }: {
      email: string;
      password: string;
      name: string;
      role: AdminRole;
    }) => {
      // Create admin_users record via RPC (upserts if already exists)
      const { data, error } = await supabase.rpc('create_admin_user' as never, {
        p_email: email,
        p_name: name,
        p_role: role,
      } as never);

      if (error) {
        console.error('Failed to create admin_users record:', error);
        throw error;
      }

      // Try to create auth user (ignore if already exists)
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, role },
        },
      });

      if (authError && !authError.message.includes('already been registered')) {
        throw authError;
      }

      return data as unknown as AdminUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<AdminUser, 'name' | 'role' | 'is_active'>>;
    }) => {
      const { data, error } = await supabase.rpc('update_admin_user' as never, {
        p_id: id,
        p_name: updates.name ?? null,
        p_role: updates.role ?? null,
        p_is_active: updates.is_active ?? null,
      } as never);

      if (error) throw error;
      return data as unknown as AdminUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Soft delete by setting is_active to false
      const { error } = await supabase.rpc('update_admin_user' as never, {
        p_id: id,
        p_name: null,
        p_role: null,
        p_is_active: false,
      } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

